import { createContext, useContext, ReactNode } from 'react'
import { UserRole, userRoleGroups } from '@common/interfaces/user'
import { useSelector } from '../redux/hooks'
import { IRootState } from '../redux/reducer'

interface PermissionsContextProps {
  isParticipant: boolean
  isAdmin: boolean
  isUW: boolean
  isBDO: boolean
  isExtendedRightsRole: boolean
  isAdminRightsRole: boolean
  isClientUser: boolean
  isProspectUser: boolean
  isPortfolio: boolean
  isInternalUser: boolean
  /**
   * The tasks-and-messaging role: Tasks and AIM are their entire portal. Use it to
   * withhold a surface from them specifically — `isInternalUser` stays false for
   * this role, so anything already gated on that is withheld for free. Arc is the
   * main thing withheld this way inside the two surfaces they do have.
   */
  isTasksAndMessaging: boolean
  /**
   * Anyone who can use AIM — every internal role plus the tasks-and-messaging one.
   * The client-side twin of `userRoleGroups.MESSAGING`, and the right gate for
   * fetching channels or unread counts (`isInternalUser` would skip exactly the
   * role whose primary feature this is).
   */
  isMessagingUser: boolean
  /**
   * Anyone who can use Tasks — the twin of `userRoleGroups.TASKS`, and the right
   * gate for the Tasks nav badge and its seeding fetches.
   */
  isTaskUser: boolean
  /**
   * Who sees a prospect's portal-user settings: underwriting, business
   * development and admins. Narrower than what the server allows on the
   * underlying user services (which still enforce BDO scoping via
   * assertCanManageUser) — this keeps the surface off the roles that have no
   * reason to invite or revoke a prospect's login.
   */
  canManageProspectUsers: boolean
  /** Who has a Personal access tokens settings tab — the twin of `ACCESS_TOKEN_HOLDERS`. */
  canHoldAccessTokens: boolean
  /** Business development, underwriting and admins: who opens, moves and annotates deals. */
  canWorkDeals: boolean
  /** Business development and admins: who may permanently delete a never-funded prospect. */
  canDeleteProspects: boolean
}

const PermissionsContext = createContext<PermissionsContextProps | undefined>(undefined)

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const userType = useSelector((state: IRootState) => state.profile?.user?.role)
  const isProspectUser = useSelector((state: IRootState) => state.profile?.user?.isProspectUser)

  const permissions = {
    isParticipant: userType === UserRole.PARTICIPANT_USER,
    isAdmin: userType === UserRole.ADMIN,
    isUW: userType === UserRole.UW_USER,
    isBDO: userType === UserRole.BDO,
    isExtendedRightsRole: [
      UserRole.ADMIN,
      UserRole.PORTFOLIO_ADMIN,
      UserRole.PORTFOLIO_USER,
      UserRole.UW_USER,
    ].includes(userType),
    isAdminRightsRole: [UserRole.ADMIN, UserRole.PORTFOLIO_ADMIN, UserRole.UW_USER].includes(
      userType,
    ),
    isPortfolio: [UserRole.PORTFOLIO_ADMIN, UserRole.PORTFOLIO_USER].includes(userType),
    isClientUser: userType === UserRole.CLIENT_USER,
    isProspectUser: userType === UserRole.CLIENT_USER && isProspectUser,
    isInternalUser: userRoleGroups.INTERNAL.includes(userType),
    isTasksAndMessaging: userType === UserRole.TASKS_AND_MESSAGING,
    isMessagingUser: userRoleGroups.MESSAGING.includes(userType),
    isTaskUser: userRoleGroups.TASKS.includes(userType),
    canManageProspectUsers: [UserRole.ADMIN, UserRole.UW_USER, UserRole.BDO].includes(userType),
    canHoldAccessTokens: userRoleGroups.ACCESS_TOKEN_HOLDERS.includes(userType),
    canWorkDeals: userRoleGroups.BDOS.includes(userType),
    canDeleteProspects: userRoleGroups.PROSPECT_DELETERS.includes(userType),
  }

  return <PermissionsContext.Provider value={permissions}>{children}</PermissionsContext.Provider>
}

export const usePermissions = () => {
  const context = useContext(PermissionsContext)
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return context
}
